import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from '@/components/auth/AuthModal';
import { useToast } from '@/hooks/use-toast';
import DesktopNavigation from './nav/DesktopNavigation';
import MobileNavigation from './nav/MobileNavigation';
import { NavItem } from './nav/types';
import { useDjangoData } from '@/contexts/DjangoContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useDjangoData();
  const { profile, signOut, loading, checkAuthState } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleSignInSuccess = () => {
    window.location.reload();
  };

  const handleAuthClick = () => {
    if (profile) {
      handleSignOut();
    } else {
      setShowAuthModal(true);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Sign out failed",
        description: "There was a problem signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProfileClick = () => {
    if (profile) {
      navigate(`/profile/${profile.id}`);
    }
  };

  const navItems: NavItem[] = [
    { path: '/', label: 'Home' },
    { path: '/explore-rides', label: 'Explore Rides' },
    { path: '/rallies', label: 'ADV Rallies' },
    { path: '/content-garage', label: 'Content Garage' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const renderAuthModal = () => {
    return (
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onSignInSuccess={handleSignInSuccess}
        onSignUpSuccess={() => {
          setShowAuthModal(false);
          window.location.reload();
        }}
      />
    );
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass py-2' : 'bg-transparent py-3'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo - Increased size and improved spacing */}
          <NavLink to="/" className="relative z-10">
            <img 
              src={data.urls?.logo} 
              alt="Exodus ADV Logo" 
              className={`transition-all duration-300 ${isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'}`}
            />
          </NavLink>

          {/* Desktop Navigation */}
          <DesktopNavigation 
            navItems={navItems}
            profile={profile}
            loading={false}
            handleSignOut={handleSignOut}
            setShowAuthModal={setShowAuthModal}
            isScrolled={isScrolled}
          />

          {/* Mobile navigation toggle */}
          <button
            className="md:hidden relative z-10 text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle Menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile Navigation Menu */}
          <MobileNavigation 
            navItems={navItems}
            isOpen={isOpen}
            profile={profile}
            loading={false}
            handleProfileClick={handleProfileClick}
            handleSignOut={handleSignOut}
            setShowAuthModal={setShowAuthModal}
          />
        </div>
      </div>
      
      {renderAuthModal()}
    </header>
  );
};

export default Navbar;
